import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AppsOutlined,
  ArrowBack,
  BusinessOutlined,
  PersonOutlined,
  VpnKeyOutlined,
  HelpOutlineOutlined,
} from '@mui/icons-material';
import { EMAIL_RECOVERY_HELP, isValidUserEmail } from '../../../../../utils/userEmail';
import {
  createUser,
  updateUser,
  getRestaurantes,
  getUsers,
} from '../../services/api';

const emptyUser = {
  nome: '',
  username: '',
  password: '',
  is_admin: false,
  is_owner: false,
  billing_exempt: false,
  restaurante_id: '',
  gap: false,
  atendabot: false,
  clube_mesa: false,
  reservo: false,
  sales_report: false,
  feedback_hero: false,
  is_food: false,
  integracao: false,
  checklist: false,
  cv: false,
  cardapio: false,
  smart: false,
  confereai: false,
  crm: false,
  linkpage: false,
};

const PRODUCT_OPTIONS = [
  { key: 'gap',           label: 'GAP'             },
  { key: 'atendabot',     label: 'Atendabot'       },
  { key: 'clube_mesa',    label: 'Clube de Mesa'   },
  { key: 'reservo',       label: 'Reservo'         },
  { key: 'sales_report',  label: 'DataChef'        },
  { key: 'feedback_hero', label: 'Feedback Hero'   },
  { key: 'smart',         label: 'Smart Campaigns' },
  { key: 'checklist',     label: 'cheflist'        },
  { key: 'cv',            label: 'CurrículoCerto'  },
  { key: 'cardapio',      label: 'One Menu'        },
  { key: 'confereai',     label: 'ConfereAI'       },
  { key: 'crm',            label: 'ClientCenter CRM' },
  { key: 'linkpage',      label: 'LinkPage'        },
  { key: 'is_food',       label: 'É Food?'         },
];

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const makeIconBox = (color, bg, border) => ({
  width: 36,
  height: 36,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bg,
  color,
  flexShrink: 0,
  border: `1px solid ${border}`,
});

const SECTION_ICON_BOX = makeIconBox(
  '#6366f1',
  'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  'rgba(99,102,241,0.18)',
);

const productToggleSx = {
  px: 1.5,
  py: 1,
  borderRadius: '12px',
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
  border: '1px solid rgba(15,23,42,0.08)',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(99,102,241,0.32)',
    boxShadow: '0 4px 16px -8px rgba(99,102,241,0.18)',
  },
};

const normalizePayload = (form, editing) => {
  const payload = { ...form, nome: String(form.nome || '').trim(), username: String(form.username || '').trim() };
  delete payload.is_admin;
  if (payload.restaurante_id === '') payload.restaurante_id = null;
  if (editing && !String(payload.password || '').trim()) delete payload.password;
  return payload;
};

const UserFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = Boolean(id && id !== 'new');
  const [form, setForm] = useState({ ...emptyUser, ...location.state });
  const originalUsernameRef = useRef(String(location.state?.username || '').trim());
  const legacyLogin = editing && !isValidUserEmail(originalUsernameRef.current);
  const usernameChanged = !editing || String(form.username || '').trim() !== originalUsernameRef.current;
  const [restaurantes, setRestaurantes] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    let active = true;
    const loadReferenceData = async () => {
      try {
        const [restaurantData, userData] = await Promise.all([
          getRestaurantes(),
          getUsers(),
        ]);
        if (!active) return;
        setRestaurantes(Array.isArray(restaurantData) ? restaurantData : []);
        setUsers(Array.isArray(userData) ? userData : []);
        if (editing && !location.state) {
          const user = (userData || []).find(item => String(item.id) === String(id));
          if (user) {
            originalUsernameRef.current = String(user.username || '').trim();
            setForm({ ...emptyUser, ...user });
          } else {
            setSubmitError('Usuário não encontrado.');
          }
        }
      } catch (error) {
        if (active) setSubmitError(error?.response?.data?.error || 'Não foi possível carregar os dados do usuário.');
      }
    };
    loadReferenceData();
    return () => { active = false; };
  }, [editing, id, location.state]);

  const selectedOwner = users.find((user) => (
    user.is_owner === true
    && String(user.restaurante_id || '') === String(form.restaurante_id || '')
    && String(user.id) !== String(id || '')
  ));
  const isManagedUser = !form.is_admin && !form.is_owner;

  const handleChange = (name) => (event) => setForm((prev) => ({ ...prev, [name]: event.target.value }));
  const handleCheck = (name) => (event) => {
    const { checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitInFlightRef.current) return;
    const payload = normalizePayload(form, editing);
    if (!payload.nome) {
      setSubmitError('Informe o nome do funcionário.');
      return;
    }
    if (usernameChanged && !isValidUserEmail(payload.username)) {
      setSubmitError('Informe um e-mail válido para o login e a recuperação de senha.');
      return;
    }
    if (usernameChanged) payload.username = payload.username.toLowerCase();
    if (!form.is_admin && !payload.is_owner) {
      payload.billing_exempt = false;
      PRODUCT_OPTIONS.forEach((product) => {
        if (selectedOwner?.[product.key] !== true) {
          payload[product.key] = false;
        }
      });
    }
    submitInFlightRef.current = true;
    setSaving(true);
    setSubmitError('');
    try {
      if (editing) await updateUser(id, payload);
      else await createUser(payload);
      navigate(-1);
    } catch (error) {
      console.error('Erro ao salvar usuário do manager:', error);
      setSubmitError(
        error?.response?.data?.error || error?.message
          || (editing ? 'Erro ao atualizar usuário.' : 'Erro ao criar usuário.')
      );
    } finally {
      submitInFlightRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 860, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Box component="form" id="user-form" name="manager-member-editor" onSubmit={handleSubmit} autoComplete="off">

          {/* ── Buttons row (outside card) ── */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Button
              variant="text"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              disabled={saving}
              sx={{
                color: '#475569', fontWeight: 600, borderRadius: '12px', px: 1.5, py: 0.75,
                '&:hover': { background: 'rgba(99,102,241,0.08)', color: '#6366f1' },
              }}
            >
              Voltar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                borderRadius: '12px', px: 3, py: 1, fontWeight: 700, boxShadow: 'none',
                bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                transition: 'all 150ms ease',
              }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Salvar'}
            </Button>
          </Stack>

          {/* ── Card ── */}
          <Box sx={CARD_SX}>
            {/* Colored top bar */}
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />

            <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
              {/* Header */}
              <Stack direction="row" alignItems="center" gap={1.5} mb={2.5}>
                <Box sx={{ ...makeIconBox('#6366f1', 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)', 'rgba(99,102,241,0.18)'), width: 48, height: 48, borderRadius: '14px' }}>
                  <PersonOutlined sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: '1.25rem', letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.25 }}>
                    {editing ? 'Editar Usuário' : 'Novo Usuário'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {editing ? 'Atualize login, vínculo, permissões e produtos liberados.' : 'Preencha os dados para criar um novo usuário.'}
                  </Typography>
                </Box>
              </Stack>

              {submitError && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{submitError}</Alert>}

              <Stack spacing={3}>
                {/* Section: Acesso */}
                <Box>
                  <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                    <Box sx={SECTION_ICON_BOX}><VpnKeyOutlined sx={{ fontSize: 18 }} /></Box>
                    <Box>
                      <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.95rem' }}>Acesso</Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>Dados utilizados para entrar no sistema.</Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Nome do funcionário"
                        id="manager-member-display-name"
                        name="manager-member-display-name"
                        autoComplete="off"
                        size="small"
                        fullWidth
                        required
                        value={form.nome || ''}
                        onChange={handleChange('nome')}
                        disabled={saving}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="E-mail de acesso"
                        id="manager-member-access-address"
                        name="manager-member-access-address"
                        autoComplete="off"
                        type={legacyLogin ? 'text' : 'email'}
                        size="small"
                        fullWidth
                        required
                        value={form.username}
                        onChange={handleChange('username')}
                        disabled={saving}
                        helperText={editing && usernameChanged
                          ? 'Ao salvar, este e-mail passa a ser o novo login do usuário.'
                          : legacyLogin ? 'Login antigo sem e-mail. Informe um e-mail válido para habilitar a recuperação de senha.' : undefined}
                        inputProps={{ maxLength: 254, autoCapitalize: 'none', spellCheck: false }}
                        InputProps={{ endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={EMAIL_RECOVERY_HELP} enterTouchDelay={0} describeChild>
                              <IconButton type="button" size="small" aria-label="Sobre o e-mail e a recuperação de senha">
                                <HelpOutlineOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ) }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Senha" id="manager-member-initial-secret" name="manager-member-initial-secret" autoComplete="new-password" type="password" size="small" fullWidth required={!editing} value={form.password} onChange={handleChange('password')} helperText="Deixe em branco para manter a senha atual." disabled={saving} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ borderColor: 'rgba(15,23,42,0.06)' }} />

                {/* Section: Vínculo */}
                <Box>
                  <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                    <Box sx={SECTION_ICON_BOX}><BusinessOutlined sx={{ fontSize: 18 }} /></Box>
                    <Box>
                      <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.95rem' }}>Vínculo e permissões</Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>Relacione ao estabelecimento e ajuste as permissões.</Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField select label="Estabelecimento" size="small" fullWidth value={form.restaurante_id || ''} onChange={handleChange('restaurante_id')} disabled={saving} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                        <MenuItem value="">Nenhum</MenuItem>
                        {restaurantes.map((r) => <MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={productToggleSx}>
                        <FormControlLabel control={<Switch size="small" checked={Boolean(form.is_owner)} onChange={handleCheck('is_owner')} color="primary" disabled={saving || form.is_admin} />} label="Owner pagante" labelPlacement="start" sx={{ m: 0, width: '100%', justifyContent: 'space-between' }} />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={productToggleSx}>
                        <FormControlLabel control={<Switch size="small" checked={Boolean(form.billing_exempt)} onChange={handleCheck('billing_exempt')} color="primary" disabled={saving || isManagedUser} />} label="Isento de cobrança" labelPlacement="start" sx={{ m: 0, width: '100%', justifyContent: 'space-between' }} />
                      </Box>
                    </Grid>
                  </Grid>
                  {isManagedUser && form.restaurante_id && !selectedOwner && (
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: '12px' }}>
                      Este estabelecimento ainda não possui owner. Cadastre o owner pagante antes dos demais usuários.
                    </Alert>
                  )}
                  {isManagedUser && selectedOwner && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>
                      O pagamento será herdado de {selectedOwner.username}. Só os produtos liberados nesse owner podem ser atribuídos.
                    </Alert>
                  )}
                </Box>

                <Divider sx={{ borderColor: 'rgba(15,23,42,0.06)' }} />

                {/* Section: Produtos */}
                <Box>
                  <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                    <Box sx={SECTION_ICON_BOX}><AppsOutlined sx={{ fontSize: 18 }} /></Box>
                    <Box>
                      <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.95rem' }}>Produtos liberados</Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>Escolha os módulos disponíveis para este usuário.</Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
                  <Grid container spacing={1.5}>
                    {PRODUCT_OPTIONS.map((product) => (
                      <Grid item xs={12} md={6} key={product.key}>
                        <Box sx={productToggleSx}>
                          <FormControlLabel
                            control={(
                              <Switch
                                size="small"
                                checked={Boolean(form[product.key])}
                                onChange={handleCheck(product.key)}
                                color="primary"
                                disabled={
                                  saving
                                  || (
                                    isManagedUser
                                    && (!selectedOwner || selectedOwner[product.key] !== true)
                                  )
                                }
                              />
                            )}
                            label={product.label}
                            labelPlacement="start"
                            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserFormPage;